<template>
  <b-card class="cst-card" no-body>
    <div class="cst-head">
      <span class="cst-title">
        <i class="fa fa-bar-chart" aria-hidden="true"></i>
        Tickets over time
        <span class="cst-hint">raised per {{ bucketNoun.toLowerCase() }} · colored by current status</span>
      </span>
      <span class="cst-legend">
        <span class="cst-legend-item"><span class="cst-swatch cst-swatch--open"></span>Open</span>
        <span class="cst-legend-item"><span class="cst-swatch cst-swatch--scheduled"></span>Scheduled</span>
        <span class="cst-legend-item"><span class="cst-swatch cst-swatch--completed"></span>Completed</span>
        <span class="cst-legend-item"><span class="cst-linekey"></span>{{ avgLabel }}</span>
      </span>
      <span class="cst-controls">
        <span class="cst-chips" role="group" aria-label="Date range">
          <button
            v-for="r in ranges"
            :key="'r-' + r.days"
            type="button"
            class="cst-chip"
            :class="{ 'cst-chip--active': rangeDays === r.days }"
            :aria-pressed="rangeDays === r.days ? 'true' : 'false'"
            @click="rangeDays = r.days"
          >{{ r.label }}</button>
        </span>
        <span class="cst-chips" role="group" aria-label="View">
          <button type="button" class="cst-chip"
                  :class="{ 'cst-chip--active': view === 'chart' }"
                  :aria-pressed="view === 'chart' ? 'true' : 'false'"
                  @click="view = 'chart'">Chart</button>
          <button type="button" class="cst-chip"
                  :class="{ 'cst-chip--active': view === 'table' }"
                  :aria-pressed="view === 'table' ? 'true' : 'false'"
                  @click="view = 'table'">Table</button>
        </span>
      </span>
    </div>
    <div class="cst-body">
      <div v-if="!rangeTotal" class="cst-empty">
        <span>No coaching tickets raised in this range.</span>
        <b-button v-if="rangeDays !== 365" size="sm" variant="outline-secondary"
                  @click="rangeDays = 365">Try 12 months</b-button>
      </div>

      <!-- role=group, not img: img would flatten the descendants and a screen
           reader could never hear the values; the live region voices each
           arrow-key step (the ops_desk_analytics hero pattern) -->
      <div
        v-else-if="view === 'chart'"
        ref="chartBody"
        class="cst-wrap"
        tabindex="0"
        role="group"
        :aria-label="chartAria"
        @keydown.left.prevent="nudgeHover(-1)"
        @keydown.right.prevent="nudgeHover(1)"
        @keydown.esc="hoverIdx = null"
        @blur="hoverIdx = null"
        @mouseleave="hoverIdx = null"
      >
        <span class="sr-only" aria-live="polite">{{ tipText }}</span>
        <svg :width="chartW" :height="chartH">
          <g>
            <line v-for="t in yTicks" :key="'g' + t.v" class="cst-grid"
                  :x1="margins.l" :x2="chartW - margins.r" :y1="t.y" :y2="t.y" />
            <text v-for="t in yTicks" :key="'y' + t.v" class="cst-tick"
                  :x="margins.l - 8" :y="t.y + 3.5" text-anchor="end">{{ t.v.toLocaleString() }}</text>
          </g>
          <g v-for="(b, i) in cols" :key="'b' + b.bucket"
             :class="{ 'cst-lift': hoverIdx === i }">
            <path v-for="seg in b.segs" :key="seg.k" :d="seg.d"
                  :class="'cst-seg cst-seg--' + seg.k" />
          </g>
          <path v-if="avgPath" :d="avgPath" class="cst-avg" />
          <g>
            <text v-for="b in xLabels" :key="'x' + b.bucket" class="cst-tick"
                  :x="b.x" :y="chartH - 8" text-anchor="middle">{{ b.label }}</text>
          </g>
          <line v-if="hoverIdx !== null && cols[hoverIdx]" class="cst-cross"
                :x1="cols[hoverIdx].cx" :x2="cols[hoverIdx].cx"
                :y1="margins.t" :y2="chartH - margins.b" />
          <!-- hit targets: the whole column slot, never just the painted bar -->
          <rect v-for="(b, i) in cols" :key="'h' + b.bucket" class="cst-hit"
                :x="b.x0" :y="margins.t" :width="b.slotW" :height="chartH - margins.t - margins.b"
                @mouseenter="hoverIdx = i" />
        </svg>
        <div v-if="tipModel" class="cst-tip" :style="tipStyle">
          <div class="cst-tip-title">{{ tipModel.title }}</div>
          <div v-for="row in tipModel.rows" :key="row.label" class="cst-tip-row"
               :class="{ 'cst-tip-row--rule': row.rule }">
            <span class="cst-tip-key" :class="row.k ? 'cst-tip-key--' + row.k : ''"></span>
            <span class="cst-tip-val">{{ row.v }}</span>
            <span class="cst-tip-lbl">{{ row.label }}</span>
          </div>
        </div>
      </div>

      <!-- tabindex: safari does not make overflow scrollers keyboard
           focusable on its own, and this table is the chart's
           accessibility twin -->
      <div v-else class="cst-tv-scroll" tabindex="0" role="region" aria-label="Tickets over time, table view">
        <table class="cst-tv">
          <thead>
            <tr>
              <th>{{ bucketNoun }}</th>
              <th>Open</th><th>Scheduled</th><th>Completed</th><th>Total</th><th>{{ avgLabel }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(b, i) in overTime" :key="'tv' + b.bucket">
              <td>{{ bucketLabel(b.bucket) }}</td>
              <td>{{ b.open }}</td><td>{{ b.scheduled }}</td><td>{{ b.completed }}</td>
              <td>{{ b.open + b.scheduled + b.completed }}</td>
              <td>{{ avgSeries[i] }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </b-card>
</template>

<script>
import moment from 'moment'

// the Coaching Dashboard's hero: the ops_desk_analytics "Events over time"
// chart re-serieed by ticket status. everything computes client-side from the
// already-loaded queue (created_at buckets a ticket; its CURRENT status
// colors it), so the widget needs no api call of its own. series colors match
// the Queue status bars further down the page (#dc3545 / #d9a84e / #2f7d32)
// so status wears one color everywhere on this dashboard; the counts always
// also appear as text (tooltip + table twin), so color never gates a value.

const STATUS_KEYS = ['open', 'scheduled', 'completed']

export default {
  name: 'coaching_status_timeline',

  props: {
    // coaching tickets only — the parent excludes recognition, like every
    // other coaching figure on the dashboard
    tickets: { type: Array, required: true },
  },

  data() {
    return {
      rangeDays: 90,
      ranges: [
        { days: 30, label: '30d' },
        { days: 90, label: '90d' },
        { days: 365, label: '12mo' },
      ],
      view: 'chart',
      hoverIdx: null,
      chartW: 960,
    }
  },

  computed: {
    // daily buckets up to 90d, weekly for 12mo — the ops_desk_analytics
    // hero's granularity; the width-aware x-label thinning below keeps 90
    // daily columns readable
    granularity() {
      return this.rangeDays <= 90 ? 'day' : 'week'
    },
    bucketNoun() {
      return this.granularity === 'week' ? 'Week' : 'Day'
    },
    avgLabel() {
      return this.granularity === 'week' ? '4-week avg' : '7-day avg'
    },
    bucketDefs() {
      const g = this.granularity
      // UTC on both sides: created_at is a UTC ISO stamp, so the buckets must
      // be UTC too or boundary-evening tickets fall between buckets (the
      // monthlyFlow precedent on the parent page)
      const unit = g === 'week' ? 'isoWeek' : g
      const end = moment.utc().startOf(unit)
      const cur = moment.utc().subtract(this.rangeDays - 1, 'days').startOf(unit)
      const defs = []
      while (cur.isSameOrBefore(end)) {
        defs.push(cur.format('YYYY-MM-DD'))
        cur.add(1, g)
      }
      return defs
    },
    overTime() {
      const byBucket = {}
      this.bucketDefs.forEach(b => {
        byBucket[b] = { bucket: b, open: 0, scheduled: 0, completed: 0 }
      })
      this.tickets.forEach(t => {
        if (!t.created_at || !STATUS_KEYS.includes(t.status)) return
        const row = byBucket[this.bucketKey(t.created_at)]
        if (row) row[t.status] += 1
      })
      return this.bucketDefs.map(b => byBucket[b])
    },
    bucketTotals() {
      return this.overTime.map(b => b.open + b.scheduled + b.completed)
    },
    rangeTotal() {
      return this.bucketTotals.reduce((a, b) => a + b, 0)
    },
    // trailing mean over the last few buckets; the line answers "which way
    // is this going" over the per-bucket spikes
    avgSeries() {
      const w = this.granularity === 'week' ? 4 : 7
      const t = this.bucketTotals
      return t.map((_, i) => {
        const from = Math.max(0, i - w + 1)
        const slice = t.slice(from, i + 1)
        return Math.round((slice.reduce((a, b) => a + b, 0) / slice.length) * 10) / 10
      })
    },

    // ---- chart geometry (the ops_desk_analytics hero recipe) ----
    margins() {
      return { l: 40, r: 12, t: 12, b: 26 }
    },
    chartH() {
      return 240
    },
    yMax() {
      const raw = Math.max(1, ...this.bucketTotals)
      // ticket counts are integers, so the axis never steps finer than 1
      const step = Math.max(1, this.niceStep(raw / 4))
      return { step, max: Math.ceil(raw / step) * step }
    },
    yTicks() {
      const { step, max } = this.yMax
      const ticks = []
      for (let v = 0; v <= max; v += step) ticks.push({ v, y: this.yFor(v) })
      return ticks
    },
    cols() {
      const m = this.margins
      const n = this.overTime.length
      if (!n) return []
      const plotW = Math.max(50, this.chartW - m.l - m.r)
      const slotW = plotW / n
      const barW = Math.max(2, Math.min(24, slotW - Math.max(2, slotW * 0.25)))
      const baseY = this.chartH - m.b
      return this.overTime.map((b, i) => {
        const x0 = m.l + slotW * i
        const x = x0 + (slotW - barW) / 2
        // completed carries the mass at the baseline; open — the work — tops
        // the stack where it stays visible
        const order = [
          { k: 'completed', v: b.completed },
          { k: 'scheduled', v: b.scheduled },
          { k: 'open', v: b.open },
        ].filter(s => s.v > 0)
        const topIdx = order.length - 1
        let bottom = baseY
        const segs = order.map((s, si) => {
          const h = baseY - this.yFor(s.v)
          const yTop = bottom - h
          // 2px surface gap between touching segments (never a stroke); the
          // gap comes out of the upper segment so the baseline stays square
          const gap = si > 0 ? 2 : 0
          const drawH = Math.max(h - gap, h > 0 ? 1 : 0)
          const drawBottom = bottom - gap
          const d = si === topIdx
            ? this.roundedTopPath(x, drawBottom - drawH, barW, drawH)
            : 'M' + x + ',' + (drawBottom - drawH) + 'h' + barW + 'v' + drawH + 'h-' + barW + 'Z'
          bottom = yTop
          return { k: s.k, d }
        })
        return { bucket: b.bucket, x0, slotW, cx: x0 + slotW / 2, segs }
      })
    },
    avgPath() {
      if (this.cols.length < 2) return ''
      return this.cols
        .map((b, i) => (i ? 'L' : 'M') + b.cx.toFixed(1) + ',' + this.yFor(this.avgSeries[i]).toFixed(1))
        .join(' ')
    },
    xTickEvery() {
      // thin by available width, not just bucket count: a "May 13" label
      // needs ~44px of slot or 31 daily buckets collide on a phone
      const m = this.margins
      const plotW = Math.max(50, this.chartW - m.l - m.r)
      const maxLabels = Math.max(3, Math.min(12, Math.floor(plotW / 44)))
      return Math.max(1, Math.ceil(this.overTime.length / maxLabels))
    },
    xLabels() {
      return this.cols
        .filter((_, i) => i % this.xTickEvery === 0)
        .map(b => ({ bucket: b.bucket, x: b.cx, label: this.bucketLabel(b.bucket) }))
    },
    chartAria() {
      const first = this.bucketDefs[0] || ''
      return 'Stacked ' + this.bucketNoun.toLowerCase() + 'ly coaching tickets by status with a trailing average, ' +
        first + ' to today. Use arrow keys to step through values, or switch to the table view.'
    },
    tipModel() {
      const i = this.hoverIdx
      const b = i !== null ? this.overTime[i] : null
      if (!b) return null
      return {
        title: this.tipTitle(b.bucket),
        rows: [
          { k: 'open', label: 'Open', v: b.open },
          { k: 'scheduled', label: 'Scheduled', v: b.scheduled },
          { k: 'completed', label: 'Completed', v: b.completed },
          { label: 'Total', v: b.open + b.scheduled + b.completed, rule: true },
          { k: 'avg', label: this.avgLabel, v: this.avgSeries[i] },
        ],
      }
    },
    // spoken twin of the tooltip for the aria-live region
    tipText() {
      const t = this.tipModel
      if (!t) return ''
      return t.title + ': ' + t.rows.map(r => r.v + ' ' + r.label).join(', ')
    },
    tipStyle() {
      const b = this.hoverIdx !== null ? this.cols[this.hoverIdx] : null
      if (!b) return {}
      const flip = b.cx > this.chartW / 2
      return flip
        ? { left: (b.cx - 12) + 'px', transform: 'translateX(-100%)' }
        : { left: (b.cx + 12) + 'px' }
    },
  },

  watch: {
    rangeDays() { this.hoverIdx = null },
    view() { this.$nextTick(() => this.measure()) },
  },

  mounted() {
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.measure())
      this._ro.observe(this.$el)
    } else {
      this._onResize = () => this.measure()
      window.addEventListener('resize', this._onResize)
    }
    this.measure()
  },

  updated() {
    this.measure()
  },

  beforeDestroy() {
    if (this._ro) this._ro.disconnect()
    if (this._onResize) window.removeEventListener('resize', this._onResize)
  },

  methods: {
    bucketKey(createdAt) {
      const day = String(createdAt).slice(0, 10)
      if (this.granularity === 'week') return moment.utc(day).startOf('isoWeek').format('YYYY-MM-DD')
      return day
    },
    bucketLabel(bucket) {
      return moment.utc(bucket).format('MMM D')
    },
    tipTitle(bucket) {
      const label = moment.utc(bucket).format('MMM D, YYYY')
      return this.granularity === 'week' ? 'Week of ' + label : label
    },
    measure() {
      // guard against the updated() loop: only assign on a real change
      const el = this.$refs.chartBody
      if (el && el.clientWidth && el.clientWidth !== this.chartW) this.chartW = el.clientWidth
    },
    niceStep(raw) {
      // snap an axis step to 1/2/5 x 10^k so tick values read clean
      const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 0.1))))
      for (const m of [1, 2, 5, 10]) {
        if (raw <= m * mag) return m * mag
      }
      return 10 * mag
    },
    yFor(v) {
      const m = this.margins
      const plotH = this.chartH - m.t - m.b
      return this.chartH - m.b - (v / this.yMax.max) * plotH
    },
    roundedTopPath(x, y, w, h, r) {
      // rounded data-end (top corners only); the baseline end stays square
      const rr = Math.min(r || 3, w / 2, h)
      if (rr <= 0.5) return 'M' + x + ',' + y + 'h' + w + 'v' + h + 'h-' + w + 'Z'
      return 'M' + x + ',' + (y + h) +
        'L' + x + ',' + (y + rr) +
        'Q' + x + ',' + y + ' ' + (x + rr) + ',' + y +
        'L' + (x + w - rr) + ',' + y +
        'Q' + (x + w) + ',' + y + ' ' + (x + w) + ',' + (y + rr) +
        'L' + (x + w) + ',' + (y + h) + 'Z'
    },
    nudgeHover(step) {
      const n = this.cols.length
      if (!n) return
      const cur = this.hoverIdx === null ? (step > 0 ? -1 : n) : this.hoverIdx
      this.hoverIdx = Math.max(0, Math.min(n - 1, cur + step))
    },
  },
}
</script>

<style scoped>
.cst-card {
  /* status colors shared with the Queue status bars below on the dashboard,
     so one status wears one color everywhere on the page. midnight
     re-resolves these in the unscoped block at the bottom of the file. */
  --cst-open: #dc3545;
  --cst-scheduled: #d9a84e;
  --cst-completed: #2f7d32;
  --cst-avg: #33414f;
  --cst-grid-line: #e9eef3;
}

.cst-head {
  display: flex; align-items: center; gap: 8px 14px; flex-wrap: wrap;
  padding: 10px 14px; font-weight: 600; font-size: 0.95em;
  border-bottom: 1px solid var(--fi-line, #dee2e6);
}
.cst-title { display: inline-flex; align-items: center; gap: 7px; }
.cst-title .fa { opacity: 0.7; }
.cst-hint { font-weight: 400; font-size: 0.85em; opacity: 0.7; }
.cst-body { padding: 10px 14px 8px; }

.cst-controls { display: inline-flex; gap: 10px; margin-left: auto; }
.cst-chips { display: inline-flex; gap: 4px; }
/* the cp-preset-chip recipe, compact */
.cst-chip {
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 999px;
  padding: 2px 9px; font-size: 11.5px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .12s, border-color .12s, color .12s;
}
.cst-chip:hover:not(.cst-chip--active) { background: #eef4fb; border-color: #b8c8da; }
.cst-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
.cst-chip:focus-visible { outline: 2px solid var(--fi-ops-accent, #3a6e99); outline-offset: 1px; }

/* series identity: swatch mirrors the mark (rect for fills, line for lines) */
.cst-legend { display: inline-flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.cst-legend-item {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11.5px; font-weight: 600; color: var(--fi-text-muted, #6b7a8c);
}
.cst-swatch { display: inline-block; width: 10px; height: 10px; border-radius: 3px; flex: none; }
.cst-swatch--open { background: var(--cst-open); }
.cst-swatch--scheduled { background: var(--cst-scheduled); }
.cst-swatch--completed { background: var(--cst-completed); }
.cst-linekey {
  display: inline-block; width: 14px; height: 2px; border-radius: 2px;
  background: var(--cst-avg);
}

.cst-empty {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  padding: 48px 0; color: var(--fi-text-muted, #8a97a5);
}

.cst-wrap { position: relative; outline: none; }
.cst-wrap:focus-visible { border-radius: 8px; box-shadow: 0 0 0 2px var(--fi-ops-accent, #3a6e99); }
.cst-grid { stroke: var(--cst-grid-line); stroke-width: 1; }
.cst-tick {
  fill: var(--fi-text-muted, #6b7a8c);
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
}
.cst-seg--open { fill: var(--cst-open); }
.cst-seg--scheduled { fill: var(--cst-scheduled); }
.cst-seg--completed { fill: var(--cst-completed); }
.cst-lift .cst-seg { filter: brightness(1.12); }
.cst-avg {
  fill: none;
  stroke: var(--cst-avg);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.cst-cross {
  stroke: color-mix(in srgb, var(--fi-text-muted, #6b7a8c) 55%, transparent);
  stroke-width: 1;
}
.cst-hit { fill: transparent; }

.cst-tip {
  position: absolute; top: 10px; z-index: 5;
  min-width: 150px; padding: 8px 12px 9px;
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(20, 40, 60, 0.18);
  pointer-events: none;
}
.cst-tip-title {
  font-size: 11px; font-weight: 700;
  color: var(--fi-text-muted, #6b7a8c);
  margin-bottom: 4px;
}
.cst-tip-row {
  display: grid; grid-template-columns: 14px 34px 1fr;
  gap: 6px; align-items: center;
  font-size: 12.5px; line-height: 1.55;
}
.cst-tip-row--rule {
  border-top: 1px solid var(--fi-line, #e3e9ef);
  margin-top: 3px; padding-top: 3px;
}
/* value leads, label follows: the reader has the series, wants the number */
.cst-tip-val {
  font-weight: 700; text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--fi-text, #2c3a49);
}
.cst-tip-lbl { color: var(--fi-text-muted, #6b7a8c); }
.cst-tip-key { height: 3px; border-radius: 2px; }
.cst-tip-key--open { background: var(--cst-open); }
.cst-tip-key--scheduled { background: var(--cst-scheduled); }
.cst-tip-key--completed { background: var(--cst-completed); }
.cst-tip-key--avg { background: var(--cst-avg); }

.cst-tv-scroll { max-height: 240px; overflow: auto; }
/* separate + box-shadow, not collapse + border: collapsed borders detach
   from a position:sticky th and scroll away with the body */
.cst-tv { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12.5px; }
.cst-tv th {
  position: sticky; top: 0;
  background: var(--fi-surface, #f8f9fa);
  text-align: right; font-weight: 700;
  color: var(--fi-text-muted, #6b7a8c);
  padding: 4px 10px;
  box-shadow: 0 1px 0 var(--fi-line, #d6dde6);
}
.cst-tv th:first-child { text-align: left; }
.cst-tv td {
  text-align: right;
  font-variant-numeric: tabular-nums;
  padding: 3px 10px;
  border-bottom: 1px solid color-mix(in srgb, var(--fi-line, #e3e9ef) 55%, transparent);
  color: var(--fi-text, #2c3a49);
}
.cst-tv td:first-child { text-align: left; color: var(--fi-text-muted, #6b7a8c); }
</style>

<style>
/* midnight re-inks. global block (not scoped) because the data-fi-theme
   attribute lives on <html> and scoped selectors can't reach ancestors.
   series steps are the ops_desk_analytics dark palette (validated against
   the midnight card surface), not a lightened copy of the light values. */
:root[data-fi-theme='midnight'] .cst-card {
  --cst-open: #e46666;
  --cst-scheduled: #bd8524;
  --cst-completed: #27a075;
  --cst-avg: #cfdae5;
  --cst-grid-line: #2c3b4a;
}
</style>
